/**
 * Pricing Estimation Service
 *
 * Handles pricing estimation API calls for the widget order builder.
 * Provides real-time pricing calculations based on container selection,
 * material type, and additional services.
 */

import { apiClient, ApiClient } from './api';
import type { EstimatePriceRequest, EstimatePriceResponse } from '@/types/pricing-estimation';

/**
 * PricingEstimationService class
 *
 * Provides methods to estimate pricing for container orders.
 * Public widget endpoint - no authentication required.
 */
export class PricingEstimationService {
  private client: ApiClient;

  constructor(client: ApiClient = apiClient) {
    this.client = client;
  }

  /**
   * Estimate price for a container order
   *
   * @param providerSlug - Provider slug (e.g., 'paso-rolloff')
   * @param params - Pricing estimation parameters
   * @returns Promise<EstimatePriceResponse> - Complete pricing estimate with fees and warnings
   * @throws {ApiError} - If estimation fails or invalid parameters
   */
  async estimatePrice(
    providerSlug: string,
    params: EstimatePriceRequest
  ): Promise<EstimatePriceResponse> {
    return this.client.post<EstimatePriceResponse>(
      `/widget/${providerSlug}/pricing/estimate`,
      params
    );
  }
}

// Export singleton instance
export const pricingEstimationService = new PricingEstimationService();
